import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "font-medium text-sm rounded-lg",
    "transition-all duration-[180ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-40",
    "active:scale-[0.98]",
  ].join(" "),
  {
    variants: {
      variant: {
        /** CTA de dinheiro/conversão: ouro radiante com glow fintech */
        gold: [
          "bg-[hsl(var(--gold))] text-[hsl(var(--gold-foreground))]",
          "font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.5),_0_0_20px_rgba(251,191,36,0.25)]",
          "hover:bg-[hsl(var(--gold-glow))] hover:shadow-[0_0_30px_rgba(251,191,36,0.4)] hover:brightness-105",
          "focus-visible:ring-[hsl(var(--gold))]",
        ].join(" "),

        /** CTA principal: esmeralda elétrico com glow */
        default: [
          "bg-primary text-primary-foreground font-semibold",
          "shadow-[0_1px_2px_rgba(0,0,0,0.5),_0_0_20px_rgba(16,185,129,0.25)]",
          "hover:bg-[hsl(var(--primary-glow))] hover:shadow-[0_0_30px_rgba(16,185,129,0.4)] hover:brightness-105",
        ].join(" "),

        /** CTA de marca (landing): lima marca-texto com texto tinta */
        signal: [
          "bg-signal text-signal-foreground font-semibold",
          "shadow-[inset_0_1px_0_rgba(255,255,255,0.4),0_10px_28px_-12px_hsl(var(--signal)/0.6)]",
          "hover:brightness-110",
          "focus-visible:ring-signal",
        ].join(" "),

        /** Botão secundário: superfície elevada com hairline sutil */
        secondary: [
          "bg-[hsl(var(--surface-raised))] text-foreground border border-[hsl(var(--border-strong))]",
          "shadow-[0_1px_2px_rgba(0,0,0,0.4),_inset_0_1px_0_0_rgba(255,255,255,0.06)]",
          "hover:border-primary/50 hover:bg-[hsl(var(--surface-overlay))] hover:text-primary",
        ].join(" "),

        /** Contorno: transparente com borda */
        outline: [
          "border border-[hsl(var(--border-strong))] bg-transparent text-foreground",
          "hover:bg-[hsl(var(--surface-raised))] hover:border-primary/50 hover:text-foreground",
        ].join(" "),

        /** Contorno ouro */
        "outline-gold": [
          "border border-[hsl(var(--gold)/0.45)] bg-transparent text-[hsl(var(--gold))]",
          "hover:bg-[hsl(var(--gold)/0.1)] hover:border-[hsl(var(--gold)/0.7)]",
          "focus-visible:ring-[hsl(var(--gold))]",
        ].join(" "),

        /** Ghost: sem fundo, texto discreto */
        ghost: [
          "text-muted-foreground",
          "hover:text-foreground hover:bg-[hsl(var(--surface-raised))]",
        ].join(" "),

        /** Destrutivo: vermelho alta precisão */
        destructive: [
          "bg-destructive text-destructive-foreground font-medium",
          "shadow-[0_1px_2px_rgba(0,0,0,0.4)] hover:bg-destructive/90",
        ].join(" "),

        /** Link: texto com underline */
        link: "text-primary underline-offset-4 hover:underline p-0 h-auto",
      },
      size: {
        default:  "h-9 px-4 py-2",
        sm:       "h-8 px-3 text-xs rounded-md",
        lg:       "h-11 px-6 text-base rounded-lg",
        icon:     "h-9 w-9 p-0",
        "icon-sm":"h-7 w-7 p-0 text-xs rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
